// pages/ArticleDetail.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import TransitionLink from '../components/TransitionLink';
import { useArticles } from '../context/articlesConfig';
import SEO from '../components/SEO';

const categoryColors = {
    Broadcast: 'text-orange-400 border-orange-500/30',
    Code: 'text-blue-400 border-blue-500/30',
    '3D': 'text-purple-400 border-purple-500/30',
    Personal: 'text-green-400 border-green-500/30',
};

const ArticleDetail = () => {
    const { id } = useParams();
    const { getArticleById, getNextArticle } = useArticles();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showBackToTop, setShowBackToTop] = useState(false);
    const contentRef = useRef(null);

    useEffect(() => {
        setLoading(true);
        const found = getArticleById(id);
        setArticle(found || null);
        setLoading(false);

        const scrollToTop = () => {
            if (window.lenis) {
                window.lenis.scrollTo(0, { immediate: true });
            } else {
                window.scrollTo(0, 0);
            }
        };
        setTimeout(scrollToTop, 100);
    }, [id, getArticleById]);

    useEffect(() => {
        const handleScroll = () => setShowBackToTop(window.scrollY > 400);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleBackToTop = () => {
        if (window.lenis) {
            window.lenis.scrollTo(0, {
                duration: 2.0,
                easing: (t) => 1 - Math.pow(1 - t, 5),
            });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
                <div className="text-white text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: 'easeInOut' }}
                    >
                        <div className="w-16 h-16 mx-auto mb-6 relative">
                            <div className="absolute inset-0 border-2 border-white/20 rounded-full"></div>
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                className="absolute inset-0 border-2 border-accent border-t-transparent rounded-full"
                            />
                        </div>
                    </motion.div>
                </div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="min-h-screen bg-black text-white flex flex-col">
                <Header />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="text-4xl mb-6">Article not found</h1>
                        <TransitionLink to="/articles" className="text-accent hover:underline">
                            Back to Articles
                        </TransitionLink>
                    </div>
                </div>
            </div>
        );
    }

    const nextArticle = getNextArticle(id);

    // Render body: split on --- for dividers, \n\n for paragraphs, **text** for bold
    const renderBody = (text) => {
        return text.split('\n\n').map((block, i) => {
            if (block.trim() === '---') {
                return <hr key={i} className="border-gray-800 my-10" />;
            }
            if (block.startsWith('**') && block.endsWith('**')) {
                return (
                    <h3
                        key={i}
                        className="text-xl font-bold text-white mt-10 mb-4"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                    >
                        {block.replace(/\*\*/g, '')}
                    </h3>
                );
            }
            // Inline bold within paragraph
            const parts = block.split(/(\*\*[^*]+\*\*)/g);
            return (
                <p key={i} className="text-gray-300 leading-relaxed mb-6 text-lg">
                    {parts.map((part, j) =>
                        part.startsWith('**') && part.endsWith('**')
                            ? <strong key={j} className="text-white font-semibold">{part.replace(/\*\*/g, '')}</strong>
                            : part
                    )}
                </p>
            );
        });
    };

    return (
        <div ref={contentRef} className="min-h-screen bg-black text-white">
            <SEO
                title={article.title}
                description={article.excerpt}
                url={`/articles/${article.id}`}
                type="article"
            />
            <Header isAboutDark={true} />

            {/* Hero */}
            <section className="pt-32 pb-16">
                <div className="container mx-auto px-8 md:px-16 max-w-4xl">
                    {/* Breadcrumb */}
                    <nav className="flex items-center space-x-2 text-sm text-white/50 mb-10">
                        <TransitionLink to="/" className="hover:text-white transition-colors">Home</TransitionLink>
                        <span>/</span>
                        <TransitionLink to="/articles" className="hover:text-white transition-colors">Articles</TransitionLink>
                        <span>/</span>
                        <span className="text-white/80 truncate max-w-xs">{article.title}</span>
                    </nav>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                    >
                        {/* Meta */}
                        <div className="flex flex-wrap items-center gap-4 mb-8">
                            <span className={`text-xs px-3 py-1 rounded border ${categoryColors[article.category] || 'text-gray-400 border-gray-600'}`}>
                                {article.category}
                            </span>
                            <span className="text-sm text-gray-500 font-mono">{formatDate(article.date)}</span>
                            <span className="text-sm text-gray-600">{article.readTime} read</span>
                        </div>

                        {/* Title */}
                        <h1
                            className="text-4xl md:text-6xl font-bold tracking-tight leading-tight mb-8"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            {article.title}
                        </h1>

                        {/* Excerpt */}
                        <p className="text-xl text-gray-400 leading-relaxed border-l-4 border-accent pl-6">
                            {article.excerpt}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Body */}
            <section className="pb-24">
                <div className="container mx-auto px-8 md:px-16 max-w-3xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
                    >
                        {renderBody(article.body)}
                    </motion.div>

                    {/* Tags */}
                    {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-16 pt-10 border-t border-gray-800">
                            {article.tags.map(tag => (
                                <span key={tag} className="px-3 py-1 text-xs bg-gray-800 text-gray-400 rounded-full font-mono">
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Next Article */}
            {nextArticle && nextArticle.id !== article.id && (
                <section className="py-20 border-t border-gray-800">
                    <div className="container mx-auto px-8 md:px-16 max-w-4xl">
                        <div className="text-center mb-8">
                            <span className="text-sm text-gray-500 uppercase tracking-wide">Next Article</span>
                        </div>
                        <TransitionLink
                            to={`/articles/${nextArticle.id}`}
                            className="group block"
                        >
                            <div className="relative overflow-hidden rounded-xl border border-gray-800 hover:border-accent transition-all duration-500 p-8 md:p-10">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-4">
                                            <span className={`text-xs px-2 py-0.5 rounded border ${categoryColors[nextArticle.category] || 'text-gray-400 border-gray-600'}`}>
                                                {nextArticle.category}
                                            </span>
                                            <span className="text-xs text-gray-600">{nextArticle.readTime}</span>
                                        </div>
                                        <h3
                                            className="text-2xl md:text-3xl font-bold mb-3 group-hover:text-accent transition-colors tracking-tight"
                                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                        >
                                            {nextArticle.title}
                                        </h3>
                                        <p className="text-gray-400 leading-relaxed">{nextArticle.excerpt}</p>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <svg
                                            className="w-8 h-8 text-accent group-hover:translate-x-2 transition-transform duration-300"
                                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </TransitionLink>
                    </div>
                </section>
            )}

            {/* Back to Top */}
            {showBackToTop && (
                <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={handleBackToTop}
                    className="fixed bottom-8 right-8 w-12 h-12 bg-accent hover:bg-accent/90 text-black rounded-full flex items-center justify-center shadow-lg transition-colors z-40"
                    aria-label="Back to top"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                </motion.button>
            )}
        </div>
    );
};

export default ArticleDetail;
